import { useMemo } from 'react';

import { useSystemHealth, useExchangesHealth } from '@/api/hooks/use-system';
import { useNotificationStore } from '@/shared/store/notification-store';

export interface Notification {
  id: string;
  type: 'system' | 'bot' | 'exchange';
  severity: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: string;
  link?: string;
  /** Interpolation params for i18n title/message (e.g. { name: "BTC Scalper" }) */
  tParams?: Record<string, string>;
}

export function useNotifications() {
  const { data: health } = useSystemHealth();
  const { data: exchangesHealth } = useExchangesHealth();
  const lastSeenAt = useNotificationStore((s) => s.lastSeenAt);

  const notifications = useMemo<Notification[]>(() => {
    const items: Notification[] = [];
    const now = new Date().toISOString();

    // System halted — critical
    if (health?.system_halted) {
      items.push({
        id: 'system-halted',
        type: 'system',
        severity: 'critical',
        title: 'notifications.systemHalted',
        message: 'notifications.systemHaltedDesc',
        timestamp: now,
      });
    }

    // Bots halted or crashed — warning per bot
    if (health?.bots) {
      for (const bot of health.bots) {
        if (bot.runtime_state === 'halted') {
          items.push({
            id: `bot-halted-${bot.bot_id}`,
            type: 'bot',
            severity: 'warning',
            title: 'notifications.botHalted',
            message: 'notifications.botHaltedDesc',
            timestamp: bot.last_heartbeat_at ?? now,
            link: `/bots/${bot.bot_id}`,
            tParams: { name: bot.bot_name },
          });
        } else if (bot.runtime_state === 'crashed') {
          items.push({
            id: `bot-crashed-${bot.bot_id}`,
            type: 'bot',
            severity: 'warning',
            title: 'notifications.botCrashed',
            message: 'notifications.botCrashedDesc',
            timestamp: bot.last_heartbeat_at ?? now,
            link: `/bots/${bot.bot_id}`,
            tParams: { name: bot.bot_name },
          });
        }
      }
    }

    // Exchange health — exchanges not healthy
    if (exchangesHealth?.exchanges) {
      for (const exchange of exchangesHealth.exchanges) {
        if (exchange.state !== 'HEALTHY') {
          items.push({
            id: `exchange-down-${exchange.exchange}`,
            type: 'exchange',
            severity: exchange.state === 'DOWN' ? 'critical' : 'warning',
            title: 'notifications.exchangeDown',
            message: 'notifications.exchangeDownDesc',
            timestamp: exchange.checked_at,
            link: `/system`,
            tParams: { name: exchange.exchange },
          });
        }
      }
    }

    // Sort by severity (critical first) then by timestamp (newest first)
    const severityOrder: Record<Notification['severity'], number> = {
      critical: 0,
      warning: 1,
      info: 2,
    };
    items.sort((a, b) => {
      const sDiff = (severityOrder[a.severity] ?? 2) - (severityOrder[b.severity] ?? 2);
      if (sDiff !== 0) return sDiff;
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });

    return items;
  }, [health, exchangesHealth]);

  const unreadCount = useMemo(() => {
    if (!lastSeenAt) return notifications.length;
    const seenTime = new Date(lastSeenAt).getTime();
    return notifications.filter((n) => new Date(n.timestamp).getTime() > seenTime).length;
  }, [notifications, lastSeenAt]);

  return { notifications, unreadCount };
}
